import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useClientData, loadClientYearData } from './hooks/useClientData';
import DrilldownSidebar from 'components/DrilldownSidebar/DrilldownSidebar';
import MapDashboard from 'components/MapDashboard/MapDashboard';

import SideChartPanel from 'components/SideChartPanel/SideChartPanel';
import ComparePanel from 'components/ComparePanel/ComparePanel';
import { fetchClientsConfig, fetchYearsConfig, setClientsConfig, setYearsConfig, getClientCityLoadPlan, getClientCities, findClientConfig } from './services/companyFileReader';
import { fetchCurrentUser } from './services/APIs';
import { isEmbedded, notifyHost } from './utils/embedBridge';
import styles from './App.module.css';

/** Multi-city clients start on "Tutte"; single-city clients on their only city. */
const resolveDefaultCity = (company) => {
  const cities = getClientCities(company);
  return cities.length === 1 ? cities[0] : null;
};

/**
 * App — root component driving the full application screen flow.
 *
 * Authentication: requires ?auth_token=... in URL or localStorage.
 *
 * State machine:
 *  - isInitializingAuth → loading screen
 *  - finalError         → error screen
 *  - currentCompany     → main dashboard layout
 *
 * Dashboard layout:
 *  - DrilldownSidebar (left, dark)
 *  - MapDashboard     (centre, fullscreen Leaflet)
 *  - SideChartPanel   (overlays map left, when activeSub selected and !compareMode)
 *  - ComparePanel     (overlays map left, when compareMode active)
 */
const App = () => {
  // ── Auth state ────────────────────────────────────────────────────────────
  const [isInitializingAuth, setIsInitializingAuth] = useState(true);
  const [userCompany, setUserCompany]             = useState(null);
  const [allCompanies, setAllCompanies]           = useState([]);
  const [availableYears, setAvailableYears]       = useState([]);

  // ── Drill-down navigation state ───────────────────────────────────────────
  const [selectedYear, setSelectedYear]           = useState(null);
  const [selectedCity, setSelectedCity]           = useState(null);
  const [activeMacro, setActiveMacro]             = useState(null);
  const [activeSub, setActiveSub]                 = useState(null);
  const [activeBreakdown, setActiveBreakdown]     = useState(null);
  const [compareMode, setCompareMode]             = useState(false);
  const [isSidebarOpen, setIsSidebarOpen]         = useState(false);
  const [mapRevealed, setMapRevealed]             = useState(false);

  // ── Auth effect ─────────────────────────────────────────────────────────
  useEffect(() => {
    const initializeAuthAndData = async () => {
      try {
        // 1. Resolve auth token and requested client from URL or localStorage
        const params = new URLSearchParams(window.location.search);
        const hostCompany = (params.get('mc_client') || params.get('company') || '').trim();
        let token = params.get('auth_token');
        if (token) {
          localStorage.setItem('auth_token', token);
        } else {
          token = localStorage.getItem('auth_token');
        }
        if (params.has('auth_token') || params.has('mc_client') || params.has('company')) {
          const newUrl = window.location.pathname + window.location.hash;
          window.history.replaceState({}, document.title, newUrl);
        }

        // 2. Fetch clients.json and years.json from uploads API
        const clientsConfig = await fetchClientsConfig();
        setClientsConfig(clientsConfig);

        const years = await fetchYearsConfig();
        setYearsConfig(years);

        const sortedYears = [...years].sort((a, b) => b - a);
        setAvailableYears(sortedYears);

        // 3. Resolve the company to display. A host page (Traffix) tells us which
        // client it opened, so that wins whenever it maps to a known config.
        // Use the clients.json name so admin-panel aliases resolve to real data.
        let company = (hostCompany && findClientConfig(hostCompany)?.company) || null;

        if (!company && token) {
          try {
            const user = await fetchCurrentUser();
            if (user && user.company) {
              company = user.company;
            }
          } catch {
            // Auth /me may be unavailable in the iframe; fall through to localStorage.
          }
        }

        // Fallback if no token or user company retrieval failed
        if (!company) {
          company = localStorage.getItem('company') || 'ATB';
        }

        localStorage.setItem('company', company);

        // 4. Fetch comparison data — one JSON per city, merged per client/year
        const loaded = [];
        for (const c of clientsConfig) {
          if (getClientCityLoadPlan(c).length === 0) continue;
          for (const year of sortedYears) {
            try {
              const merged = await loadClientYearData(c, year);
              if (merged) loaded.push(merged);
            } catch {
              // Skip missing year/city comparison files.
            }
          }
        }

        const validLoaded = loaded.filter(Boolean);
        setAllCompanies(validLoaded);
        setUserCompany(company);

        // Default to the most recent year from years.json (not from JSON file content)
        if (sortedYears.length > 0) {
          setSelectedYear(sortedYears[0]);
        }

      } catch {
        setUserCompany('ATB');
      } finally {
        setIsInitializingAuth(false);
      }
    };

    initializeAuthAndData();
  }, []);

  // ── Data resolving ────────────────────────────────────────────────────────
  
  const currentCompany = allCompanies.find(
    c => c.anno === selectedYear && c.azienda.toUpperCase() === userCompany?.toUpperCase()
  ) || null;

  const { geojsonData } = useClientData(userCompany, selectedYear, selectedCity);

  // Multi-city → Tutte (null); single city → that city
  useEffect(() => {
    setSelectedCity(resolveDefaultCity(userCompany));
    setActiveMacro(null);
    setActiveSub(null);
    setActiveBreakdown(null);
    setCompareMode(false);
  }, [userCompany, selectedYear]);

  const resetToDefaultView = useCallback(() => {
    setSelectedCity(resolveDefaultCity(userCompany));
    setActiveMacro(null);
    setActiveSub(null);
    setActiveBreakdown(null);
    setCompareMode(false);
    setIsSidebarOpen(false);
  }, [userCompany]);

  // ── Host frame (Traffix) integration ──────────────────────────────────────
  // True while the drill-down sidebar or a side panel covers the map's left edge.
  const leftPanelOpen = isSidebarOpen || compareMode || Boolean(activeMacro || activeSub);

  // The host may only learn which client is logged in after this app has booted,
  // so it can hand the client over at any time instead of via the URL.
  const applyHostCompany = useCallback((hostCompany) => {
    const conf = hostCompany ? findClientConfig(hostCompany) : null;
    if (!conf) return;
    setUserCompany((prev) => {
      if (prev && prev.toUpperCase().trim() === conf.company.toUpperCase().trim()) return prev;
      localStorage.setItem('company', conf.company);
      return conf.company;
    });
  }, []);

  // The host keeps this app mounted between visits, so it asks for a clean
  // start each time it becomes visible instead of reusing the previous drill-down.
  useEffect(() => {
    if (!isEmbedded()) return undefined;

    const onHostMessage = (event) => {
      const type = event?.data?.type;
      if (
        type === 'mystery-client:show'
        || type === 'mystery-client:reset'
        || type === 'mystery-client:client'
      ) {
        applyHostCompany(event.data.company);
      }

      if (type === 'mystery-client:show' || type === 'mystery-client:reset') {
        resetToDefaultView();
      } else if (type === 'mystery-client:request-state') {
        notifyHost('mystery-client:sidebar', { open: leftPanelOpen });
      }
    };

    window.addEventListener('message', onHostMessage);
    return () => window.removeEventListener('message', onHostMessage);
  }, [applyHostCompany, resetToDefaultView, leftPanelOpen]);

  // Lets the host move its own overlays (e.g. its logo) out of the sidebar's way.
  useEffect(() => {
    notifyHost('mystery-client:sidebar', { open: leftPanelOpen });
  }, [leftPanelOpen]);

  const isInitializing = isInitializingAuth || (allCompanies.length === 0);
  const finalError     = null; // removed dataError dependency to allow pure allCompanies browsing

  useEffect(() => {
    if (isInitializing) return;
    notifyHost('mystery-client:ready', { company: userCompany });
  }, [isInitializing, userCompany]);

  // ── Loading Progress Simulation ────────────────────────────────────────────
  const [progress, setProgress] = useState(0);
  const [showMainApp, setShowMainApp] = useState(false);

  useEffect(() => {
    if (finalError) {
      setShowMainApp(true);
      return;
    }

    let interval;
    let timer;

    if (!isInitializing) {
      // Fast-forward to 100% once loading finishes
      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            timer = setTimeout(() => setShowMainApp(true), 250);
            return 100;
          }
          return Math.min(prev + 4, 100);
        });
      }, 16);
    } else {
      // Increment up to 95% while loading
      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 95) return 95;
          const diff = 95 - prev;
          const step = Math.max(0.5, diff * 0.08);
          return Math.min(prev + step, 95);
        });
      }, 80);
    }

    return () => {
      clearInterval(interval);
      if (timer) clearTimeout(timer);
    };
  }, [isInitializing, finalError]);

  // ── Handlers ──────────────────────────────────────────────────────────────


  const handleMacroChange = (macroId) => {
    setActiveMacro(macroId);
    setActiveSub(null);
    setActiveBreakdown(null);
    if (!macroId) setCompareMode(false);
  };

  const handleSubChange = (subKey) => {
    setActiveSub(subKey);
    setActiveBreakdown(null);
  };

  const handleBreakdownChange = (bdKey) => {
    setActiveBreakdown(bdKey);
  };

  const handleToggleCompare = () => {
    setCompareMode((prev) => !prev);
  };

  const handleCitySelect = (city) => {
    setSelectedCity(city);
    // Preserve activeMacro, activeSub, activeBreakdown and compareMode
    // so the user stays at the same navigation depth when switching cities.
  };

  const handleToggleSidebar = () => {
    if (isSidebarOpen) {
      setIsSidebarOpen(false);
      setActiveMacro(null);
      setActiveSub(null);
      setActiveBreakdown(null);
      setCompareMode(false);
    } else {
      setIsSidebarOpen(true);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────

  const showSideChartPanel = (activeMacro || activeSub) && !compareMode;
  const showComparePanel   = compareMode;

  // ── Error screen ──────────────────────────────────────────────────────────
  if (finalError) {
    return (
      <div className={styles.loadingScreen}>
        <div style={{ textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-sans)', padding: '0 2rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>
          <div style={{ fontSize: '1.125rem', fontWeight: 500 }}>{finalError}</div>
          <div style={{ fontSize: '0.875rem', marginTop: '1rem', opacity: 0.6 }}>
            Esempio URL: ?auth_token=123
          </div>
        </div>
      </div>
    );
  }

  // ── Loading screen ────────────────────────────────────────────────────────
  if (!currentCompany) {
    return <LoadingOverlay progress={progress} />;
  }

  // ── Main layout ───────────────────────────────────────────────────────────
  return (
    <div className={styles.appContainer}>
      {/* ── Left sidebar ── */}
      <DrilldownSidebar
        allCompanies={allCompanies}
        selectedYear={selectedYear}
        availableYears={availableYears}
        onYearChange={setSelectedYear}
        userCompany={userCompany}
        currentCompany={currentCompany}
        selectedCity={selectedCity}
        onCitySelect={handleCitySelect}
        activeMacro={activeMacro}
        activeSub={activeSub}
        activeBreakdown={activeBreakdown}
        onMacroChange={handleMacroChange}
        onSubChange={handleSubChange}
        onBreakdownChange={handleBreakdownChange}
        compareMode={compareMode}
        onToggleCompare={handleToggleCompare}
        isOpen={isSidebarOpen}
        onToggle={handleToggleSidebar}
        mapRevealed={mapRevealed}
      />

      {/* ── Map area (takes remaining space) ── */}
      <div className={styles.mapArea}>
        {/* Side panels that overlay the map from the left */}
        <AnimatePresence>
          {showComparePanel && (
            <motion.div
              key="compare-panel"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 26, stiffness: 240 }}
              className={styles.overlayPanel}
              style={{ left: isSidebarOpen ? 'clamp(300px, 30vw, 480px)' : '0', transition: 'left 0.3s ease' }}
            >
              <ComparePanel
                company={currentCompany}
                allCompanies={allCompanies}
                activeMacro={activeMacro || 'indice_sintetico'}
                activeSub={activeSub}
                activeBreakdown={activeBreakdown}
                selectedCity={selectedCity}
                onClose={() => setCompareMode(false)}
              />
            </motion.div>
          )}

          {showSideChartPanel && (
            <motion.div
              key="chart-panel"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 26, stiffness: 240 }}
              className={styles.overlayPanel}
              style={{ left: isSidebarOpen ? 'clamp(300px, 30vw, 480px)' : '0', transition: 'left 0.3s ease' }}
            >
              <SideChartPanel
                company={currentCompany}
                macro={activeMacro}
                activeSub={activeSub}
                selectedCity={selectedCity}
                onClose={() => { setActiveSub(null); setActiveMacro(null); }}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* The map itself */}
        <MapDashboard
          company={currentCompany}
          gtfsLines={geojsonData}
          activeMacro={activeMacro}
          selectedCity={selectedCity}
          userCompany={userCompany}
          onCitySelect={handleCitySelect}
          compareMode={compareMode}
          onToggleCompare={handleToggleCompare}
          isSidebarOpen={isSidebarOpen}
          onRevealChange={setMapRevealed}
        />
      </div>

      {/* Loading Screen Overlay rendered on top of everything if showMainApp is false */}
      {!showMainApp && <LoadingOverlay progress={progress} />}
    </div>
  );
};

// ── Reusable Loading Overlay Component ────────────────────────────────────────
const LoadingOverlay = ({ progress }) => {
  const roundedProgress = Math.round(progress);
  return (
    <div className={styles.loadingScreenOverlay}>
      <div className={styles.loadingBox}>
        <div className={styles.spinner}>
          <div className={styles.dot}></div>
          <div className={styles.dot}></div>
          <div className={styles.dot}></div>
          <div className={styles.dot}></div>
        </div>
        <div className={styles.loadingText}>Caricamento dati e mappa…</div>
        <div className={styles.progressContainer}>
          <div className={styles.progressTrack}>
            <div className={styles.progressBar} style={{ width: `${roundedProgress}%` }}></div>
          </div>
          <span className={styles.progressPercent}>{roundedProgress}%</span>
        </div>
      </div>
    </div>
  );
};

export default App;
